import ActionResult from 'app/src/engine/action-results/ActionResult';
import MoveMovableItemActionResult, {
	MoveMovableItemActionResultDirection,
	MoveMovableItemActionResultResult,
	MoveMovableItemActionResultType,
} from 'app/src/engine/action-results/MoveMovableItemActionResult';
import InstructionSegment, {
	InstructionSegmentStatus,
} from 'app/src/engine/instructions/InstructionSegment';
import MovableItem, {
	MovableItemLocation,
	MovableItemOrientation,
} from 'app/src/engine/movable-items/MovableItem';
import TileWalls from 'app/src/engine/tiles/TileWalls';
import GameStateStore from 'app/src/game/GameStateStore';

export enum MoveInstructionSegmentType {
	DriveForward,
	DriveBackward,
}

export default class MoveInstructionSegment extends InstructionSegment {
	private _type: MoveInstructionSegmentType;
	get type(): MoveInstructionSegmentType {
		return this._type;
	}

	constructor(type: MoveInstructionSegmentType) {
		super();

		this._type = type;
	}

	public getActionResults(
		gameStateStore: GameStateStore,
		movableItem: MovableItem,
	): { status: InstructionSegmentStatus; actionResults: ActionResult[] } {
		const movableItemLocation = gameStateStore.movableItemLocations.get(movableItem)!;

		const movableItemsPushed: MovableItem[] = [];

		let movementOrientation = MoveMovableItemActionResultDirection.Up;

		if (this._type === MoveInstructionSegmentType.DriveForward) {
			switch (movableItemLocation.orientation) {
				case MovableItemOrientation.Up:
					movementOrientation = MoveMovableItemActionResultDirection.Up;
					break;
				case MovableItemOrientation.Right:
					movementOrientation = MoveMovableItemActionResultDirection.Right;
					break;
				case MovableItemOrientation.Down:
					movementOrientation = MoveMovableItemActionResultDirection.Down;
					break;
				case MovableItemOrientation.Left:
					movementOrientation = MoveMovableItemActionResultDirection.Left;
					break;
			}
		} else if (this._type === MoveInstructionSegmentType.DriveBackward) {
			switch (movableItemLocation.orientation) {
				case MovableItemOrientation.Up:
					movementOrientation = MoveMovableItemActionResultDirection.Down;
					break;
				case MovableItemOrientation.Right:
					movementOrientation = MoveMovableItemActionResultDirection.Left;
					break;
				case MovableItemOrientation.Down:
					movementOrientation = MoveMovableItemActionResultDirection.Up;
					break;
				case MovableItemOrientation.Left:
					movementOrientation = MoveMovableItemActionResultDirection.Right;
					break;
			}
		}

		const movementObstructed = this._isMovementObstructed(
			gameStateStore,
			movableItemLocation.row,
			movableItemLocation.col,
			movementOrientation,
			movableItemsPushed,
		);
		const segmentStatus = movementObstructed
			? InstructionSegmentStatus.CancelFutureSegments
			: InstructionSegmentStatus.Success;

		const actionResults: ActionResult[] = [];

		{
			const actionResult = new MoveMovableItemActionResult(movableItem, movableItemLocation);
			actionResult.newLocation = this._getNewLocation(
				gameStateStore,
				movableItem,
				movementOrientation,
				movementObstructed,
			);
			actionResult.movementType = MoveMovableItemActionResultType.Drive;
			actionResult.movementResult = movementObstructed
				? MoveMovableItemActionResultResult.Bounce
				: MoveMovableItemActionResultResult.Move;
			actionResult.movementDirection = movementOrientation;

			// Ask the tiles if they should be transform the action result.
			actionResult.askTileToTransform(gameStateStore);

			actionResults.push(actionResult);
		}

		for (const movableItemPushed of movableItemsPushed) {
			const actionResult = new MoveMovableItemActionResult(movableItemPushed, movableItemLocation);
			actionResult.newLocation = this._getNewLocation(
				gameStateStore,
				movableItemPushed,
				movementOrientation,
				movementObstructed,
			);
			actionResult.movementType = MoveMovableItemActionResultType.Pushed;
			actionResult.movementResult = movementObstructed
				? MoveMovableItemActionResultResult.Bounce
				: MoveMovableItemActionResultResult.Move;
			actionResult.movementDirection = movementOrientation;

			// Ask the tiles if they should be transform the action result.
			actionResult.askTileToTransform(gameStateStore);

			actionResults.push(actionResult);
		}

		return { status: segmentStatus, actionResults };
	}

	private _isMovementObstructed(
		gameStateStore: GameStateStore,
		row: number,
		col: number,
		movementDirection: MoveMovableItemActionResultDirection,
		movableItemsPushed: MovableItem[],
	): boolean {
		// Check if there's a wall in the way of this robot.
		if (TileWalls.doesWallObstructMovement(gameStateStore, row, col, movementDirection)) {
			return true;
		}

		// Check if there's another robot in the way. If so, recurse.
		let nextRow = row;
		let nextCol = col;

		switch (movementDirection) {
			case MoveMovableItemActionResultDirection.Up:
				nextRow -= 1;
				break;
			case MoveMovableItemActionResultDirection.Right:
				nextCol += 1;
				break;
			case MoveMovableItemActionResultDirection.Down:
				nextRow += 1;
				break;
			case MoveMovableItemActionResultDirection.Left:
				nextCol -= 1;
				break;
		}

		if (
			nextRow >= 0 &&
			nextRow < gameStateStore.rows &&
			nextCol >= 0 &&
			nextCol < gameStateStore.cols
		) {
			const movableItemAtLocation = this._getMovableItem(gameStateStore, nextRow, nextCol);
			if (movableItemAtLocation) {
				movableItemsPushed.push(movableItemAtLocation);
				return this._isMovementObstructed(
					gameStateStore,
					nextRow,
					nextCol,
					movementDirection,
					movableItemsPushed,
				);
			}
		}

		return false;
	}

	private _getNewLocation(
		gameStateStore: GameStateStore,
		movableItem: MovableItem,
		direction: MoveMovableItemActionResultDirection,
		movementObstructed: boolean,
	): MovableItemLocation {
		const movableItemLocation = gameStateStore.movableItemLocations.get(movableItem)!;

		if (movementObstructed) {
			return movableItemLocation;
		} else {
			let nextRow = movableItemLocation.row;
			let nextCol = movableItemLocation.col;

			switch (direction) {
				case MoveMovableItemActionResultDirection.Up:
					nextRow -= 1;
					break;
				case MoveMovableItemActionResultDirection.Right:
					nextCol += 1;
					break;
				case MoveMovableItemActionResultDirection.Down:
					nextRow += 1;
					break;
				case MoveMovableItemActionResultDirection.Left:
					nextCol -= 1;
					break;
			}

			return {
				row: nextRow,
				col: nextCol,
				orientation: movableItemLocation.orientation,
				turns: movableItemLocation.turns,
			};
		}
	}

	private _getMovableItem(
		gameStateStore: GameStateStore,
		row: number,
		col: number,
	): MovableItem | null {
		for (const movableItem of gameStateStore.movableItems) {
			const movableItemLocation = gameStateStore.movableItemLocations.get(movableItem)!;
			if (movableItemLocation.row === row && movableItemLocation.col === col) {
				return movableItem;
			}
		}

		return null;
	}
}
